import {RequestState} from "modules/enums";
import {createReducer} from "redux-act";
import {
	createLeague,
	createLeagueSuccess,
	createLeagueFailed,
	clearCurrenLeague,
	fetchMyLeagues,
	fetchMyLeaguesSuccess,
	fetchMyLeaguesFailed,
	clearMyLeaguesList,
	fetchLeaguesToJoin,
	fetchLeaguesToJoinSuccess,
	fetchLeaguesToJoinFailed,
	clearJoinLeaguesList,
	fetchLeagueById,
	fetchLeagueByIdSuccess,
	fetchLeagueByIdFailed,
	updateLeague,
	updateLeagueFailed,
	updateLeagueSuccess,
	clearCurrenLeagueError,
	deleteLeague,
	deleteLeagueSuccess,
	deleteLeagueFailed,
	leaveLeague,
	leaveLeagueSuccess,
	leaveLeagueFailed,
	fetchLeagueUsers,
	fetchLeagueUsersSuccess,
	fetchLeagueUsersFailed,
	clearLeagueUsers,
	removeLeagueUser,
	removeLeagueUserSuccess,
	removeLeagueUserFailed,
	joinToLeagueSuccess,
	joinToLeague,
	joinToLeagueFailed,
	type ILeagueUser
} from "modules/actions/leagues";
import type {ILeague, IMyLeaguesResponse, IJoinToLeaguesResponse} from "modules/types/leagues";
import {fetchLeaderboardFailed} from "modules/actions/leaderboards";

interface ILeaguesStore {
	currentLeague: ILeague | null;
	leaguesList: IMyLeaguesResponse;
	leaguesToJoinList: IJoinToLeaguesResponse;
	currentLeagueError: string | null;
	requestState: RequestState;
	leagueUsers: {
		users: ILeagueUser[];
		nextPage: boolean;
	};
}

const defaultState: ILeaguesStore = {
	currentLeague: null,
	leaguesList: {
		leagues: [],
		nextPage: false,
	},
	leaguesToJoinList: {
		leagues: [],
		nextPage: false,
	},
	currentLeagueError: null,
	requestState: RequestState.IDLE,
	leagueUsers: {
		users: [],
		nextPage: false,
	},
};

export const leagues = createReducer({}, defaultState)
	.on(createLeague, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(createLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.RECEIVED,
	}))
	.on(createLeagueFailed, (state, payload) => ({
		...defaultState,
		currentLeagueError: payload.message,
		requestState: RequestState.RECEIVED,
	}))
	.on(clearCurrenLeague, (state) => ({
		...state,
		currentLeague: null,
		currentLeagueError: null,
	}))
	.on(clearCurrenLeagueError, (state) => ({
		...state,
		currentLeagueError: null,
	}));

leagues
	.on(updateLeague, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(updateLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.RECEIVED,
	}))
	.on(updateLeagueFailed, (state) => ({
		...state,
		requestState: RequestState.ERROR,
	}));

leagues
	.on(fetchMyLeagues, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(fetchMyLeaguesSuccess, (state, payload) => ({
		...state,
		leaguesList: payload,
		requestState: RequestState.RECEIVED,
	}))
	.on(fetchMyLeaguesFailed, () => ({
		...defaultState,
		requestState: RequestState.ERROR,
	}))
	.on(clearMyLeaguesList, (state) => ({
		...state,
		leaguesList: {
			leagues: [],
			nextPage: false,
		},
		requestState: RequestState.IDLE,
	}));

leagues
	.on(fetchLeaguesToJoin, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(fetchLeaguesToJoinSuccess, (state, payload) => ({
		...state,
		leaguesToJoinList: payload,
		requestState: RequestState.RECEIVED,
	}))
	.on(fetchLeaguesToJoinFailed, () => ({
		...defaultState,
		requestState: RequestState.ERROR,
	}))
	.on(clearJoinLeaguesList, (state) => ({
		...state,
		leaguesList: {
			leagues: [],
			nextPage: false,
		},
		requestState: RequestState.IDLE,
	}));

leagues
	.on(fetchLeagueById, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(fetchLeagueByIdSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.RECEIVED,
	}))
	.on(fetchLeagueByIdFailed, () => ({
		...defaultState,
		requestState: RequestState.ERROR,
	}))
	.on(fetchLeaderboardFailed, () => ({
		...defaultState,
		requestState: RequestState.ERROR,
	}));

leagues
	.on(joinToLeague, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(joinToLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.RECEIVED,
	}))
	.on(joinToLeagueFailed, () => ({
		...defaultState,
		requestState: RequestState.ERROR,
	}));

leagues
	.on(deleteLeague, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(deleteLeagueSuccess, (state) => ({
		...state,
		currentLeague: null,
		requestState: RequestState.RECEIVED,
	}))
	.on(deleteLeagueFailed, (state) => ({
		...state,
		requestState: RequestState.ERROR,
	}));

leagues
	.on(leaveLeague, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(leaveLeagueSuccess, (state) => ({
		...state,
		currentLeague: null,
		requestState: RequestState.RECEIVED,
	}))
	.on(leaveLeagueFailed, (state) => ({
		...state,
		requestState: RequestState.ERROR,
	}));

leagues
	.on(fetchLeagueUsers, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(fetchLeagueUsersSuccess, (state, {users, nextPage}) => ({
		...state,
		leagueUsers: {
			...state.leagueUsers,
			users,
			nextPage,
		},
		requestState: RequestState.RECEIVED,
	}))
	.on(fetchLeagueUsersFailed, (state) => ({
		...state,
		requestState: RequestState.ERROR,
	}))
	.on(clearLeagueUsers, (state) => ({
		...state,
		leagueUsers: {
			users: [],
			nextPage: false,
		},
	}));

leagues
	.on(removeLeagueUser, (state) => ({
		...state,
		requestState: RequestState.REQUESTED,
	}))
	.on(removeLeagueUserSuccess, (state, {userId}) => ({
		...state,
		leagueUsers: {
			...state.leagueUsers,
			users: state.leagueUsers.users.filter((user) => user.id !== userId),
		},
		requestState: RequestState.RECEIVED,
	}))
	.on(removeLeagueUserFailed, (state) => ({
		...state,
		requestState: RequestState.ERROR,
	}));
